import { IMissionDefinition, MISSIONS } from '../../Constants/Mission';
import DateHelper from '../../Utility/DateHelper';
import { Random } from '../../Utility/Random';
import {
    TActionType,
    TransactionType,
} from '../../Utility/Tracking/DataSet/Transaction';
import { trackTransaction } from '../../Utility/Tracking/TrackingHelpers/Transaction';
import { IGameState } from '../Game/IGameState';
import { IMissionData, MissionType } from './IMission';
import { IPlayerState } from './IPlayerState';
import { unlockNextBackCard } from './PlayerBackcardHelper';
import { setCoins, setKeys } from './PlayerInventoryHelper';

const MISSION_SLOTS = 4;
const MISSION_COOLDOWN = DateHelper.hoursToMilliseconds(8);
const EVENT_COOLDOWN = DateHelper.daysToMilliseconds(7);

export function updateMissionProgress(
    state: IPlayerState,
    type: MissionType,
    value: number
): IPlayerState {
    if (
        type === MissionType.CLEAR_CARD_CLUBS ||
        type === MissionType.CLEAR_CARD_DIAMONDS ||
        type === MissionType.CLEAR_CARD_HEARTS ||
        type === MissionType.CLEAR_CARD_SPADES
    ) {
        state = updateMissionProgress(state, MissionType.CLEAR_CARD_ANY, value);
    }

    // Look for this mission.
    const i = state.missions.findIndex(x => x.type === type);
    if (i === -1) return state;

    // Update mission progress.
    const missions = state.missions.slice();
    missions[i].progress = Math.min(
        missions[i].target,
        missions[i].progress + value
    );

    return {
        ...state,
        missions: missions,
    };
}

export function createMissions(state: IPlayerState): IPlayerState {
    if (state.missionTimer !== null) return state;

    // Clone current missions and fill empty slots.
    const missions: IMissionData[] = state.missions.concat();

    for (let i = missions.length; i < MISSION_SLOTS; i++) {
        missions.push({
            type: MissionType.EMPTY,
            target: 0,
            progress: 0,
            prize: 0,
        });
    }

    // Create a new mission for every empty one.
    for (let i = 0; i < MISSION_SLOTS; i++) {
        if (missions[i].type === MissionType.EMPTY) {
            missions[i] = getNewMission(missions);
        }
    }

    state = {
        ...state,
        missions: missions,
    };

    return updateMissionPrizes(state);
}

export function completeMission(
    state: IGameState,
    mission: number
): IGameState {
    const prize = state.playerState.missions[mission].prize;

    if (isMissionPrizeCoins(prize)) {
        state.playerState = setCoins(
            state.playerState,
            state.playerState.coins + prize
        );
        trackTransaction(
            state,
            TransactionType.REWARD,
            TActionType.DAILY_MISSION
        );
    } else {
        state.playerState = setKeys(
            state.playerState,
            state.playerState.keys + prize
        );
    }
    const missions = state.playerState.missions.slice();
    missions[mission].type = MissionType.EMPTY; // Set as EMPTY
    state.playerState = {
        ...state.playerState,
        missions: missions,
    };

    state.playerState = updateMissionTimer(state.playerState);

    // If there are more than 20 keys, so complete weekly event.
    if (state.playerState.keys >= 20) {
        state = completeEvent(state);
    }

    return state;
}

export function updateMissionTimer(state: IPlayerState): IPlayerState {
    // If all mission slots are filled, stop the mission timer.
    if (isFull(state.missions)) {
        return {
            ...state,
            missionTimer: null,
        };
    }

    // The player has at least one mission (slot) available and the timer is not set - start the timer.
    if (state.missionTimer === null) {
        return {
            ...state,
            missionTimer: DateHelper.now().toString(),
        };
    }

    // Six hours has elapsed. Create new missions.
    if (DateHelper.isTimeUp(state.missionTimer, MISSION_COOLDOWN)) {
        state = {
            ...state,
            missionTimer: null,
        };

        return createMissions(state);
    }

    // No change.
    return state;
}

export function updateKeyMissionTimer(state: IPlayerState): IPlayerState {
    if (state.keyMissionTimer === null) {
        // Set a new date to the next Thursday.
        const date = DateHelper.wrapDayOfWeek(4, DateHelper.now());

        return {
            ...state,
            keyMissionTimer: date.toString(),
        };
    }

    const now = DateHelper.now();
    const keyMissionTimerDate = DateHelper.getDateFromValue(
        state.keyMissionTimer
    );
    const timeDiff = keyMissionTimerDate.getTime() - now.getTime();

    if (
        timeDiff < 0 ||
        (timeDiff < EVENT_COOLDOWN && timeDiff + 1000 > EVENT_COOLDOWN)
    ) {
        // Reset keys, missions timer and create new missions.
        state = setKeys(state, 0);
        state = {
            ...state,
            missionTimer: null,
            keyMissionTimer: null,
        };
        state = updateKeyMissionTimer(state);
    }

    return createMissions(state);
}

export function skipMission(
    state: IPlayerState,
    missionIndex: number
): IPlayerState {
    state = {
        ...state,
        missions: state.missions.map((value, index) => {
            if (index === missionIndex) {
                return getNewMission(state.missions);
            }
            return value;
        }),
    };

    return updateMissionPrizes(state);
}

export function completeEvent(state: IGameState): IGameState {
    state = { ...state };
    state = unlockNextBackCard(state);
    state.playerState = setKeys(state.playerState, 0);

    // Set to the "next-next" thursday.
    const now = DateHelper.now();
    now.setDate(now.getDate() + 7);
    const date = DateHelper.wrapDayOfWeek(4, now);

    state.playerState = {
        ...state.playerState,
        keyMissionTimer: date.toString(),
    };

    return { ...state, playerState: updateMissionPrizes(state.playerState) };
}

export function getNewMission(currentMissions: IMissionData[]): IMissionData {
    const currentMissionTypes = currentMissions.map(current => current.type);

    const filteredMissions = MISSIONS.filter(
        mission => currentMissionTypes.indexOf(mission.type) === -1
    );

    const nextMission =
        filteredMissions[Random.int(0, filteredMissions.length - 1)];

    return {
        type: nextMission.type,
        prize: Random.sign() > 0 ? nextMission.keys : nextMission.coins,
        progress: 0,
        target: nextMission.target,
    };
}

/**
 * Return true if there is no empty mission slot.
 * @param missions
 */
export function isFull(missions: IMissionData[]): boolean {
    return missions.filter(x => x.type === MissionType.EMPTY).length === 0;
}

export function updateMissionPrizes(state: IPlayerState): IPlayerState {
    const missions: IMissionData[] = state.missions.concat();

    // If player have already completed the weekly event, keys become
    // useless. So, convert all missions with keys as prizes to coins.
    if (isEventCompleted(state)) {
        missions.forEach(mission => {
            if (isMissionPrizeKeys(mission.prize)) {
                const missionDef: IMissionDefinition = MISSIONS.find(
                    x => x.type === mission.type && x.target === mission.target
                );
                mission.prize = missionDef ? missionDef.coins : 35;
            }
        });
    }

    return {
        ...state,
        missions: missions,
    };
}

/**
 * If event (key hunt) timer is 7 days or more, it means that the player
 * have already completed the event and should wait until the next Thursday
 * to start a new event.
 * @param state
 */
export function isEventCompleted(state: IPlayerState): boolean {
    if (state.keyMissionTimer === null) return false;

    const now = DateHelper.now();
    const keyMissionDate = DateHelper.getDateFromValue(state.keyMissionTimer);
    const timeDiff = keyMissionDate.getTime() - now.getTime();

    return timeDiff > EVENT_COOLDOWN;
}

/**
 * Legacy:
 * The array that store the missions (IPlayerData.missions) does not differentiate
 * between coins and keys. It only considers that if the value is greater than 10
 * then it's coins otherwise it's keys.
 * @param value
 */
export function isMissionPrizeCoins(value: number): boolean {
    return value > 10;
}

/**
 * Legacy:
 * The array that store the missions (IPlayerData.missions) does not differentiate
 * between coins and keys. It only considers that if the value is greater than 10
 * then it's coins otherwise it's keys.
 * @param value
 */
export function isMissionPrizeKeys(value: number): boolean {
    return !isMissionPrizeCoins(value);
}
